import React from "react";
import moment from "moment";
import { styled } from "@material-ui/styles";
import { injectIntl } from "react-intl";
import { IStyledArguments } from "../../types/styled-arguments";
import { IMatch } from "../../types";
import Link from "../link";
import useIsManager from "../../hooks/useIsManager";
import { getMatchResult } from "../molecules/cards";
import messages from "./MatchSummaryCard.intl";

/*
 * Fila de partido dentro del "extracto" de la temporada (ver Matches.tsx) —
 * lenguaje de lista de movimientos, no una card por partido. Ver
 * DESIGN_SYSTEM.md: el marcador real es la cifra protagonista, coloreada
 * por resultado; la diferencia de goles va debajo, en pequeño.
 */

const rowStyle = (theme: IStyledArguments["theme"]) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.6, 2.25),
  borderBottom: `1px solid ${theme.palette.extra.borderColor}`,
  textDecoration: "none",
  color: "inherit",
  width: "100%",
  boxSizing: "border-box" as const,
});

const RowDiv = styled("div")(({ theme }: IStyledArguments) => rowStyle(theme));
const RowLink = styled(Link)(({ theme }: IStyledArguments) => rowStyle(theme));

const PendingRowDiv = styled(RowDiv)(({ theme }: IStyledArguments) => ({
  background: theme.palette.extra.accentSoft,
}));
const PendingRowLink = styled(RowLink)(({ theme }: IStyledArguments) => ({
  background: theme.palette.extra.accentSoft,
}));

const DateBadge = styled("div")<{ $pending?: boolean }>(
  ({ theme, $pending }: IStyledArguments & { $pending?: boolean }) => ({
    width: 40,
    height: 40,
    borderRadius: 10,
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    background: $pending ? theme.palette.primary.main : theme.palette.extra.backgroundGrey,
    border: $pending ? "none" : `1px solid ${theme.palette.extra.borderColor}`,
  })
);

const DateDay = styled("span")<{ $pending?: boolean }>(
  ({ theme, $pending }: IStyledArguments & { $pending?: boolean }) => ({
    fontFamily: theme.typography.alternateFontFamily,
    fontWeight: 700,
    fontSize: "0.8rem",
    lineHeight: 1,
    color: $pending ? "#FFFFFF" : theme.palette.text.primary,
  })
);

const DateMonth = styled("span")<{ $pending?: boolean }>(
  ({ theme, $pending }: IStyledArguments & { $pending?: boolean }) => ({
    fontSize: "0.5rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: 0.3,
    color: $pending ? "rgba(255, 255, 255, 0.85)" : theme.palette.text.secondary,
  })
);

const Body = styled("div")({
  flex: 1,
  minWidth: 0,
});

const TitleLine = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 6,
});

const VenueBadge = styled("span")<{ $pending?: boolean }>(
  ({ theme, $pending }: IStyledArguments & { $pending?: boolean }) => ({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 18,
    height: 18,
    borderRadius: 5,
    flexShrink: 0,
    background: $pending ? "#FFFFFF" : theme.palette.extra.backgroundGrey,
    border: $pending
      ? "1px solid rgba(14, 124, 134, 0.3)"
      : `1px solid ${theme.palette.extra.borderColor}`,
    fontFamily: theme.typography.alternateFontFamily,
    fontSize: "0.6rem",
    fontWeight: 700,
    color: $pending ? theme.palette.primary.dark : theme.palette.text.secondary,
  })
);

const StatusTag = styled("span")(({ theme }: IStyledArguments) => ({
  fontSize: "0.56rem",
  fontWeight: 700,
  letterSpacing: 0.3,
  textTransform: "uppercase",
  color: theme.palette.primary.dark,
  background: "rgba(14, 124, 134, 0.14)",
  borderRadius: 4,
  padding: "1px 5px",
}));

const OpponentName = styled("span")(({ theme }: IStyledArguments) => ({
  fontSize: "0.9rem",
  fontWeight: 700,
  color: theme.palette.text.primary,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

const MetaLine = styled("div")(({ theme }: IStyledArguments) => ({
  fontSize: "0.73rem",
  color: theme.palette.text.secondary,
  marginTop: 3,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

const OwnGoalNote = styled("span")(({ theme }: IStyledArguments) => ({
  color: theme.palette.extra.loss,
}));

const ScoreBlock = styled("div")({
  textAlign: "right",
  flexShrink: 0,
});

const ScoreMain = styled("div")<{ $result: "win" | "draw" | "loss" }>(
  ({ theme, $result }: IStyledArguments & { $result: "win" | "draw" | "loss" }) => ({
    fontFamily: theme.typography.alternateFontFamily,
    fontWeight: 800,
    fontSize: "1.15rem",
    lineHeight: 1,
    fontVariantNumeric: "tabular-nums",
    color: (theme.palette.extra as any)[$result],
  })
);

const ScoreDelta = styled("div")<{ $result: "win" | "draw" | "loss" }>(
  ({ theme, $result }: IStyledArguments & { $result: "win" | "draw" | "loss" }) => ({
    fontSize: "0.63rem",
    fontWeight: 600,
    marginTop: 3,
    fontVariantNumeric: "tabular-nums",
    color: (theme.palette.extra as any)[$result],
  })
);

const Chevron = styled("svg")(({ theme }: IStyledArguments) => ({
  color: theme.palette.text.secondary,
  opacity: 0.55,
  flexShrink: 0,
}));

const CtaPill = styled("span")(({ theme }: IStyledArguments) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  fontFamily: theme.typography.alternateFontFamily,
  fontWeight: 600,
  fontSize: "0.72rem",
  color: "#FFFFFF",
  background: theme.palette.primary.main,
  borderRadius: 8,
  padding: theme.spacing(0.85, 1.4),
  flexShrink: 0,
}));

const formatScorers = (match: IMatch) =>
  (match.scorerList || [])
    .filter((s) => s.goals > 0)
    .map((s) => `${s.player.name} ${s.player.surname.charAt(0)}.${s.goals > 1 ? ` ×${s.goals}` : ""}`)
    .join(", ");

interface IMatchLedgerRowProps {
  id: string;
  match: IMatch;
  currentUser: {
    id: string;
    role: string;
  };
}

const MatchLedgerRow = injectIntl<IMatchLedgerRowProps>(
  ({ id, match, currentUser, intl }) => {
    const isManager = useIsManager(currentUser.role);
    const isPending = match.status !== "FINISHED";
    const venue = match.isHomeTeamLocal
      ? intl.formatMessage(messages.home)
      : intl.formatMessage(messages.away);
    const day = moment(match.date).format("DD");
    const month = moment(match.date).format("MMM");

    if (isPending) {
      const statusLabel =
        match.status === "PREPARING"
          ? intl.formatMessage(messages.pending)
          : match.status;
      const ctaLabel =
        match.status === "PREPARING"
          ? intl.formatMessage(messages.prepareGame)
          : intl.formatMessage(messages.viewGame);
      const meta = [
        match.time,
        match.location,
        intl.formatMessage(messages.confirmed, {
          n: match.playersConfirmed.length,
        }),
      ]
        .filter(Boolean)
        .join(" · ");

      const content = (
        <>
          <DateBadge $pending>
            <DateDay $pending>{day}</DateDay>
            <DateMonth $pending>{month}</DateMonth>
          </DateBadge>
          <Body>
            <TitleLine>
              <StatusTag>{statusLabel}</StatusTag>
              <VenueBadge $pending>{venue}</VenueBadge>
            </TitleLine>
            <OpponentName style={{ display: "block", marginTop: 3 }}>
              vs {match.awayTeam}
            </OpponentName>
            <MetaLine>{meta}</MetaLine>
          </Body>
          {isManager && <CtaPill>{ctaLabel}</CtaPill>}
        </>
      );

      return isManager ? (
        <PendingRowLink to={`/matches/${id}`} data-testid={`match-row-${id}`}>
          {content}
        </PendingRowLink>
      ) : (
        <PendingRowDiv data-testid={`match-row-${id}`}>{content}</PendingRowDiv>
      );
    }

    const result = getMatchResult(match.score.home, match.score.away);
    const goalDiff = match.score.home - match.score.away;
    const goalDiffLabel = goalDiff > 0 ? `+${goalDiff}` : `${goalDiff}`;
    const scorers = formatScorers(match);
    const metaParts = [
      match.isFriendly
        ? intl.formatMessage(messages.friendly)
        : intl.formatMessage(messages.league),
      scorers,
    ].filter(Boolean);

    return (
      <RowLink to={`/matches/${id}`} data-testid={`match-row-${id}`}>
        <DateBadge>
          <DateDay>{day}</DateDay>
          <DateMonth>{month}</DateMonth>
        </DateBadge>
        <Body>
          <TitleLine>
            <VenueBadge>{venue}</VenueBadge>
            <OpponentName>{match.awayTeam}</OpponentName>
          </TitleLine>
          <MetaLine>
            {metaParts.join(" · ")}
            {match.ownGoals > 0 && (
              <>
                {" · "}
                <OwnGoalNote>
                  {intl.formatMessage(messages.ownGoal, { n: match.ownGoals })}
                </OwnGoalNote>
              </>
            )}
          </MetaLine>
        </Body>
        <ScoreBlock>
          <ScoreMain $result={result}>
            {match.score.home}&ndash;{match.score.away}
          </ScoreMain>
          <ScoreDelta $result={result}>{goalDiffLabel}</ScoreDelta>
        </ScoreBlock>
        <Chevron width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4}>
          <polyline points="9 6 15 12 9 18"></polyline>
        </Chevron>
      </RowLink>
    );
  }
);

export default MatchLedgerRow;
